import React, { useEffect, useState } from "react";
import {
  Container,
  Title,
  TextInput,
  Group,
  Text,
  Stack,
  Loader,
  Button,
  Card,
  Avatar,
  Grid,
  Pagination,
  Checkbox,
  ActionIcon,
  Menu,
  Badge,
  deepMerge,
} from "@mantine/core";
import {
  IconSearch,
  IconUserOff,
  IconMail,
  IconPhone,
  IconDots,
  IconCurrencyDollar,
  IconDotsVertical,
} from "@tabler/icons-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store";
import { fetchCourier, fetchCouriers } from "../store/slices/couriersSlice";
import { CourierResponse } from "types/trip.types";
import { hasPermission } from "utils/permissions.utils";
import { CostResponse } from "types/responses.type";
import { DEFAULT_COURIER_COSTS } from "utils/user.utils";
import CostsInputModal from "../components/CostsInputModal";
import CostService from "services/cost.service";

const Couriers = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const isAdmin = hasPermission(user, ["courier.manage"]);
  const dispatch = useDispatch<AppDispatch>();
  const { couriers, loading, error } = useSelector(
    (state: RootState) => state.couriers,
  );

  const [search, setSearch] = useState("");
  const [filteredCouriers, setFilteredCouriers] = useState<CourierResponse[]>(
    [],
  );
  const [activePage, setActivePage] = useState(1);
  const [selectedCouriers, setSelectedCouriers] = useState<Set<number>>(
    new Set(),
  );
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [costsModalOpen, setCostsModalOpen] = useState(false);
  const [courierForCosts, setCourierForCosts] =
    useState<CourierResponse | null>(null);
  const [courierInitialCosts, setCourierInitialCosts] =
    useState<CostResponse>();
  const itemsPerPage = 10;

  const paginatedCouriers = filteredCouriers.slice(
    (activePage - 1) * itemsPerPage,
    activePage * itemsPerPage,
  );

  const toggleSelectCourier = (courierId: number) => {
    setSelectedCouriers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(courierId)) {
        newSet.delete(courierId);
      } else {
        newSet.add(courierId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedCouriers.size === filteredCouriers.length) {
      setSelectedCouriers(new Set());
    } else {
      setSelectedCouriers(
        new Set(filteredCouriers.map((c) => c.courierId ?? 0)),
      );
    }
  };

  const handleSetCosts = (courierId?: number) => {
    let courier = null;
    if (courierId) {
      courier = couriers.find((c) => c.courierId === courierId) ?? null;
    }
    setCourierForCosts(courier);
    const costs = deepMerge(DEFAULT_COURIER_COSTS, courier?.costs ?? {});
    setCourierInitialCosts({
      costSubject: `COURIER:${courierId ?? 0}`,
      costs,
    });
    setCostsModalOpen(true);
  };

  const handleSaveCosts = (costsReq: CostResponse) => {
    const costRequests: CostResponse[] = [];
    if (courierForCosts) {
      costRequests.push({
        costSubject: `COURIER:${courierForCosts.courierId}`,
        costs: costsReq.costs,
      });
    } else {
      selectedCouriers.forEach((courierId) => {
        costRequests.push({
          costSubject: `COURIER:${courierId}`,
          costs: costsReq.costs,
        });
      });
    }
    // setLoading(true);
    CostService.update(costRequests)
      .then((response) => {
        if (response.ok) {
          response.data?.forEach((courierCostResponse) => {
            const courierId = Number(
              courierCostResponse.costSubject?.split(":")[1],
            );
            if (courierId) {
              dispatch(fetchCourier(courierId));
            }
          });
          // setCour(updatedVehicles);
        } else {
        }
      })
      .finally(() => {
        // setLoading(false);
      });
    setCostsModalOpen(false);
    setCourierForCosts(null);
  };
  const handleCloseCostsDialog = () => {
    setCourierInitialCosts(undefined);
    setCostsModalOpen(false);
    setCourierForCosts(null);
  };

  useEffect(() => {
    dispatch(fetchCouriers());
  }, [dispatch]);

  useEffect(() => {
    let filtered = couriers.filter((courier) => {
      const fullName = `${courier.firstName || ""} ${
        courier.lastName || ""
      }`.trim();
      const matchesSearch = fullName
        .toLowerCase()
        .includes(search.toLowerCase());
      const matchesSelected =
        !showOnlySelected || selectedCouriers.has(courier.courierId ?? 0);
      return matchesSearch && matchesSelected;
    });
    setFilteredCouriers([...filtered]);

    const totalPage = Math.ceil(filtered.length / itemsPerPage);
    if (activePage > totalPage) {
      console.log({ activePage, totalPage });
      setActivePage(1);
    }
  }, [search, couriers, showOnlySelected, selectedCouriers]);

  return (
    <Container size="xl" py="xl">
      <Stack>
        <Group justify="space-between" align="center">
          <Title order={2}>Couriers</Title>
        </Group>

        <Group justify="space-between" align="center">
          {couriers.length > 0 && (
            <TextInput
              placeholder="Search couriers..."
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              w={300}
            />
          )}
          <Pagination
            total={Math.ceil(filteredCouriers.length / itemsPerPage)}
            p={activePage}
            onChange={setActivePage}
          />
        </Group>

        {loading && <Loader />}
        {error && <Text c="red">{error}</Text>}
        {!loading && couriers.length === 0 && (
          <Stack align="center" justify="center" style={{ height: "50vh" }}>
            <IconUserOff size={48} color="gray" />
          </Stack>
        )}
        {!loading && paginatedCouriers.length > 0 && (
          <>
            {paginatedCouriers.length > 0 && (
              <Group justify="space-between" align="center" mb="md">
                <Group>
                  <Checkbox
                    label={`Select all (${filteredCouriers.length} total)`}
                    checked={selectedCouriers.size === filteredCouriers.length}
                    indeterminate={
                      selectedCouriers.size > 0 &&
                      selectedCouriers.size < filteredCouriers.length
                    }
                    onChange={toggleSelectAll}
                  />
                  {selectedCouriers.size > 0 && (
                    <>
                      <Badge
                        size="lg"
                        variant="light"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowOnlySelected(!showOnlySelected)}
                        title="Click to filter by selected couriers"
                      >
                        {selectedCouriers.size} selected
                        {showOnlySelected && " (filtered)"}
                      </Badge>
                      <Menu>
                        <Menu.Target>
                          <Button
                            leftSection={<IconDots size={16} />}
                            variant="light"
                            size="sm"
                          >
                            Actions
                          </Button>
                        </Menu.Target>
                        <Menu.Dropdown>
                          {isAdmin && (
                            <Menu.Item
                              leftSection={<IconCurrencyDollar size={14} />}
                              onClick={() => handleSetCosts()}
                            >
                              Set Costs
                            </Menu.Item>
                          )}
                        </Menu.Dropdown>
                      </Menu>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => {
                          setSelectedCouriers(new Set());
                          setShowOnlySelected(false);
                        }}
                      >
                        Clear
                      </Button>
                    </>
                  )}
                </Group>
              </Group>
            )}
            <Grid>
              {paginatedCouriers.map((courier) => (
                <Grid.Col
                  key={`courier-${courier.courierId}`}
                  span={{ base: 12, lg: 6 }}
                >
                  <Card
                    shadow="sm"
                    padding="lg"
                    radius="md"
                    withBorder
                    style={{
                      backgroundColor: selectedCouriers.has(
                        courier.courierId ?? 0,
                      )
                        ? "rgba(59, 130, 246, 0.05)"
                        : undefined,
                      borderColor: selectedCouriers.has(courier.courierId ?? 0)
                        ? "rgb(59, 130, 246)"
                        : undefined,
                      cursor: "pointer",
                    }}
                  >
                    <Group>
                      {selectedCouriers.size > 0 ? (
                        <Checkbox
                          checked={selectedCouriers.has(courier.courierId ?? 0)}
                          onChange={() =>
                            toggleSelectCourier(courier.courierId ?? 0)
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <Menu>
                          <Menu.Target>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="sm"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <IconDotsVertical size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              onClick={() =>
                                toggleSelectCourier(courier.courierId ?? 0)
                              }
                            >
                              Select
                            </Menu.Item>
                            {isAdmin && (
                              <Menu.Item
                                onClick={() =>
                                  handleSetCosts(courier.courierId ?? 0)
                                }
                              >
                                Set Cost
                              </Menu.Item>
                            )}
                          </Menu.Dropdown>
                        </Menu>
                      )}
                      <Avatar color="green" radius="xl">
                        {(courier.firstName?.[0] || "") +
                          (courier.lastName?.[0] || "")}
                      </Avatar>
                      <Stack gap={0} style={{ flex: 1 }}>
                        <Text fw={500}>
                          {[courier.firstName, courier.lastName]
                            .filter(Boolean)
                            .join(" ")}
                        </Text>
                        {courier.email && (
                          <Group gap="xs">
                            <IconMail size={16} color="gray" />
                            <Text size="sm" c="dimmed">
                              {courier.email}
                            </Text>
                          </Group>
                        )}
                        {courier.phoneNumber && (
                          <Group gap="xs">
                            <IconPhone size={16} color="gray" />
                            <Text size="sm" c="dimmed">
                              {courier.phoneNumber}
                            </Text>
                          </Group>
                        )}
                      </Stack>
                    </Group>
                  </Card>
                </Grid.Col>
              ))}
            </Grid>
          </>
        )}
      </Stack>

      <CostsInputModal
        initialCosts={courierInitialCosts}
        opened={costsModalOpen}
        onClose={handleCloseCostsDialog}
        onSave={handleSaveCosts}
        title={
          courierForCosts
            ? `Set Costs: ${[
                courierForCosts.firstName,
                courierForCosts.lastName,
              ]
                .filter(Boolean)
                .join(" ")} `
            : "Set Costs for Selected Couriers"
        }
      />
    </Container>
  );
};

export default Couriers;
